import history from '../../history';
import { createAuth0Client } from '@auth0/auth0-spa-js'

import IdTokenVerifier from 'idtoken-verifier';

/**
 * Local development auth bypass. When enabled, the app skips Auth0 entirely: you
 * start logged out, land on a local login page, and sign in as any of the seeded
 * users, no Auth0 account needed (see #3046).
 *
 * Gated three ways so it can never activate in a deployed build:
 *   - NODE_ENV === 'development' is set only by `craco start` (local dev); a
 *     production build (`craco build`, which every deployed environment uses)
 *     sets it to 'production', making this dead code that is eliminated.
 *   - REACT_APP_LOCAL_AUTH_BYPASS lives in .env.development, which builds never
 *     load.
 *   - The hostname must be localhost.
 */
export const LOCAL_AUTH_BYPASS =
  process.env.NODE_ENV === 'development' &&
  process.env.REACT_APP_LOCAL_AUTH_BYPASS === 'true' &&
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// The seeded local users you can log in as. Emails and ids match the fixtures in
// packages/backend/scripts/dynamodb_console_scripts/node_seed_local.ts.
export const LOCAL_USERS = {
  'admin@edyn.test':  { type: 'admin',  id: '351a5f10-5018-11eb-8174-0b1ef175129a' },
  'carer@edyn.test':  { type: 'carer',  id: '2e79ce10-9fbd-11e8-91f5-4feac24802b9' },
  'carer2@edyn.test': { type: 'carer',  id: '0ad46a00-dec4-11e9-943b-95100f6ca4f6' },
  'client@edyn.test': { type: 'client', id: '186e4b40-33b5-11ea-9bbe-b3dcdc47333c' },
};

const LOCAL_PORTAL = {
  admin: '/portal-admin/visits',
  carer: '/portal-carer/overview',
  client: '/portal-client/dashboard',
};

// The single shared password the local login form accepts for any seeded user.
// There are no real passwords locally (Auth0 is bypassed), so this only makes
// the local sign-in mirror the real email + password page. Override with
// REACT_APP_LOCAL_PASSWORD if desired; the default is documented in the README.
export const LOCAL_PASSWORD = process.env.REACT_APP_LOCAL_PASSWORD || 'localdev';

// Feature toggles granted to every local user.
//
// These were previously an empty array, which quietly meant local development
// exercised a DIFFERENT code path from production for anything behind a flag.
// CARE_PLAN_V2 is the one that mattered: with it off, a carer's visit screen
// renders the retired V1 care plan component instead of linking to the V2
// viewer, so the V2 medication data that the seed actually contains - and that
// EMAR is built on - was unreachable locally no matter what was seeded.
//
// Real toggles come from the Auth0 user_metadata claim (see the non-local path
// below). There is no Auth0 locally, so they are listed here instead.
//
// Add a flag here when local dev should exercise the flagged path. Leave one
// out deliberately, with a comment, when the point is to test the OFF path.
export const LOCAL_FEATURE_TOGGLES = [
  // The current care plan system. V1 is retired everywhere but the fallback
  // branches that still read it.
  'CARE_PLAN_V2',
  // The client record's Medication tab: the EMAR migration readiness view
  // (#3229). Gated because it is admin tooling for whoever is running a
  // client's migration, and because until the carer recording screen exists a
  // medication can show as scheduled with nowhere to record it.
  'EMAR',
  // The carer Overview's Today card: today's visit, and the medication round
  // once checked in. Flagged separately from EMAR because it shows EVERY
  // medication due, paper ones included, so it is useful to a carer whose client
  // has nothing on EMAR at all.
  'CARER_TODAY',
  // The carer portal on the operations shell and design system that the admin
  // portal moved to in #3271: sidebar, top bar, page headers, panels and the
  // Chakra theme for the edyn-ui pages. Off, the carer portal keeps the edyn-ui
  // layout it had before. Flagged so the two can be compared on the same
  // account and so a carer mid-round is not restyled under their thumb.
  'CARER_OPS_UI',
];

// A JWT the backend can jwt.decode (locally it decodes rather than verifies).
// Real base64url header and payload; empty signature.
//
// A deliberate escape hatch for flipping a flag locally without editing this
// file and waiting for a rebuild.
//
// Kept SEPARATE from `edyn-featureToggles`, which is what the login writes and
// what production reads. That key going stale is the bug the note in
// isFeatureEnabled describes, so it must not become authoritative again by the
// back door; this one only exists when a developer sets it on purpose, and
// clearing it returns to the declared list.
//
// In the browser console:
//
//   edynFlags.list()             what is on right now
//   edynFlags.off('CARER_TODAY') turn one off, then reload
//   edynFlags.on('CARER_TODAY')  turn one back on
//   edynFlags.reset()            drop the override entirely
//
const OVERRIDE_KEY = 'edyn-featureToggles-override'

export const localToggles = () => {
  try {
    const override = localStorage.getItem(OVERRIDE_KEY)

    return override ? JSON.parse(override) : LOCAL_FEATURE_TOGGLES
  } catch (e) {
    console.error('Ignoring an unreadable feature toggle override', e)

    return LOCAL_FEATURE_TOGGLES
  }
}

// Local only, so this cannot be reached in any build: LOCAL_AUTH_BYPASS is false
// everywhere but a developer machine.
if (LOCAL_AUTH_BYPASS && typeof window !== 'undefined') {
  const write = (flags) => {
    localStorage.setItem(OVERRIDE_KEY, JSON.stringify([...new Set(flags)]))
    console.info('Feature toggles now:', localToggles(), '- reload to apply')
  }

  window.edynFlags = {
    list: () => localToggles(),
    available: () => LOCAL_FEATURE_TOGGLES,
    on: (flag) => write([...localToggles(), flag]),
    off: (flag) => write(localToggles().filter((f) => f !== flag)),
    reset: () => {
      localStorage.removeItem(OVERRIDE_KEY)
      console.info('Override cleared. Back to the declared list:', LOCAL_FEATURE_TOGGLES)
    },
  }
}

const localBypassToken = ({ type, id, email }) => {
  const b64 = (o) =>
    btoa(JSON.stringify(o)).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  const claims = {
    // serverless-offline rejects an authorizer response with no principalId,
    // which the backend derives from `sub`, so a real Auth0 token's `sub` must
    // be present here too.
    sub: `local|${id}`,
    'http://localhost:3000/user_metadata': {},
    'http://localhost:3000/app_metadata': { type, id },
    email,
  };
  return `${b64({ alg: 'none', typ: 'JWT' })}.${b64(claims)}.`;
};

const auth0Manager = (() => {
  let auth0Client;
  let isAuth0Loading;


  const init = async function() {
    if (!auth0Client && !isAuth0Loading) {
      isAuth0Loading = true
      auth0Client = await createAuth0Client({
        domain: process.env.REACT_APP_AUTH0_DOMAIN,
        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
        useRefreshTokens: true,
        cacheLocation:'localstorage',
        authorizationParams: {
          redirect_uri: process.env.REACT_APP_AUTH0_CALLBACK_URL,
          connection: 'Username-Password-Authentication',
          audience: process.env.REACT_APP_API_AUDIENCE,
        }
      })
      isAuth0Loading = false
      return
    }
    throw new Error('Auth0 already initialised')
  }

  const getClient = function() {
    if (!auth0Client) {
      throw new Error('Auth0Client not initialised')
    }

    return auth0Client;
  }

  return {
    init,
    getClient,
  }
})()


export default class Auth {
  verifier = new IdTokenVerifier({
    issuer: process.env.REACT_APP_AUTH0_DOMAIN,
    audience: process.env.REACT_APP_AUTH0_CLIENT_ID
  });

  constructor() {
    // this.changePassword = this.changePassword.bind(this);
    this.login = this.login.bind(this);
    this.logout = this.logout.bind(this);
    this.handleAuthentication = this.handleAuthentication.bind(this);
    this.isAuthenticated = this.isAuthenticated.bind(this);
    this.getAccessToken = this.getAccessToken.bind(this);
    this.getProfile = this.getProfile.bind(this);
    this.userHasAccess = this.userHasAccess.bind(this);
    this.getUserId = this.getUserId.bind(this);
    this.handleSessionRedirect = this.handleSessionRedirect.bind(this);
  }

  async init() {
    if (LOCAL_AUTH_BYPASS) {
      // No Auth0 client and no auto-login locally; you start logged out and sign
      // in through the login page's email + password form.
      return
    }
    try {
      await auth0Manager.init()
    } catch (error) {
      console.error(error)
    }
  }

  // Seed the localStorage session for one of the seeded local users, so the app
  // is authenticated as them without ever contacting Auth0.
  seedLocalSession(email) {
    const user = LOCAL_USERS[email]
    if (!user) {
      throw new Error(`Unknown local user: ${email}`)
    }
    const token = localBypassToken({ type: user.type, id: user.id, email })
    localStorage.setItem('access_token', token)
    localStorage.setItem('id_token', token)
    localStorage.setItem('expires_at', JSON.stringify(Date.now() + 24 * 60 * 60 * 1000))
    localStorage.setItem('email', email)
    localStorage.setItem('userType', user.type)
    localStorage.setItem('userId', user.id)
    localStorage.setItem('edyn-featureToggles', JSON.stringify(localToggles()))
    localStorage.setItem('signup_status', 'complete')
  }

  // Local email + password sign-in, mirroring the real login page. Validates the
  // email against the seeded users and the shared dev password, then seeds the
  // session and lands on that user's portal. Returns null on success or an error
  // message to display. Called from the login page (login_external.js) in bypass.
  localCredentialLogin(email, password) {
    const key = (email || '').trim().toLowerCase()
    const user = LOCAL_USERS[key]
    if (!user) {
      return 'Unknown user. Use one of the seeded local emails (see the README).'
    }
    if (password !== LOCAL_PASSWORD) {
      return 'Incorrect password.'
    }
    this.seedLocalSession(key)
    window.location.assign(LOCAL_PORTAL[user.type])
    return null
  }

  async login() {
    if (LOCAL_AUTH_BYPASS) {
      // Start logged out and send the developer to the real login page, which
      // renders a local email + password form when the bypass is active.
      history.replace('/login')
      return
    }
    try {
      if (window.location.pathname === '/login-redirect') {
        await auth0Manager.getClient().loginWithRedirect()
        return
      }

      await auth0Manager.getClient().loginWithRedirect({
        appState:window.location
      })
    } catch (error) {
      console.error(error)
    }
  }

  async handleAuthentication() {
    if (LOCAL_AUTH_BYPASS) {
      // No Auth0 callback locally; send the developer to the login page.
      history.replace('/login')
      return
    }
    try {
      const auth0 = auth0Manager.getClient()
      let getAuth0CallbackState = await auth0.handleRedirectCallback()

      const getTokenResult = await auth0.getTokenSilently({detailedResponse:true});
      const userInfo = await auth0.getUser()
      this.updateLocalStorage(getTokenResult, userInfo)
      this.handleSessionRedirect(userInfo, getAuth0CallbackState?.appState)
    } catch (error) {
      console.error(error)
    }
  }

  async checkIfValidSessionExists() {
    if (LOCAL_AUTH_BYPASS) {
      // No Auth0 client to consult; a session exists only once the developer has
      // logged in as a seeded user from the local login page.
      return this.isAuthenticated()
    }
    const auth0 = auth0Manager.getClient();
    try {
      // 'off' on purpose: this is the route guard asking whether the session is
      // still good, so it must ask Auth0 rather than trust a cached token. It
      // is one exchange per navigation, not one per fetch, so it cannot race
      // itself the way the old blanket 'off' did.
      await this.getAccessTokenAsync('off')
      return true
    } catch (error) {
      return false
    }
  }

  updateLocalStorage(authResult, user) {
    let expiresAt = JSON.stringify((authResult.expires_in * 1000) + new Date().getTime());

    localStorage.setItem('access_token', authResult.access_token);
    localStorage.setItem('id_token', authResult.id_token);
    localStorage.setItem('expires_at', expiresAt);
    localStorage.setItem('email', user.email);

    // Important - the claims defined at http://localhost:3000/user_metadata and
    // http://localhost:3000/app_metadata are defined in a 'post-login flow' & action in auth0.
    // type/id live in app_metadata (not user-writable) - see packages/backend/lib/auth.ts.
    const customClaims = user['http://localhost:3000/user_metadata']
    const appClaims = user['http://localhost:3000/app_metadata'] || {}
    localStorage.setItem('userType', appClaims.type);
    localStorage.setItem('userId', appClaims.id);
    localStorage.setItem('edyn-featureToggles', JSON.stringify(customClaims.featureToggles || []))
    localStorage.setItem('signup_status', customClaims['signup_status'])
  }

  handleSessionRedirect(user, appState) {
      const loginUserType = localStorage.getItem('userType');
      const signupStatus = localStorage.getItem('signup_status')
      if (loginUserType === 'carer') {
        if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
          history.replace(appState.pathname)
          return
        }
        history.replace('/portal-carer/overview');

        return;
      }
      if (loginUserType === 'client') {
        if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
          history.replace(appState.pathname)
          return
        }
        history.replace('/portal-client/dashboard');

        return ;
      }
      if (loginUserType === 'admin') {
        if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
          history.replace(appState.pathname)
          return
        }
        history.replace('/portal-admin/visits');
        return ;
      }

    if (loginUserType === 'lead') {
      if (appState && appState.href !== `${process.env.REACT_APP_MARKETING_SITE_URL}/`) {
        history.replace(appState.pathname)
        return
      }
      history.replace('/portal-client/dashboard');
      return ;
    }

      // This would be a user who has signed up in auth0 but has not completed registration in portal
      if (loginUserType === 'incomplete' && signupStatus === 'incomplete') {
        history.replace('/find-my-carer/finish-sign-up');
        return ;
      }

      console.error(`Unkown user type on token ${JSON.stringify(user)}, extracted user type: ${loginUserType}`)

      history.replace('/');
  }

  logout() {
    // Clear access token and ID token from local storage
    localStorage.clear();

    if (window.Intercom) {
      Intercom('shutdown')
    }

    if (LOCAL_AUTH_BYPASS) {
      history.replace('/login')
      return
    }

    const auth0 = auth0Manager.getClient()

    auth0.logout({
      clientId: process.env.REACT_APP_AUTH0_CLIENT_ID,
      logoutParams:{
        returnTo: process.env.REACT_APP_MARKETING_SITE_URL,
      }
    });
  }

  signup(opts) {
    if (LOCAL_AUTH_BYPASS) {
      history.replace('/login')
      return Promise.resolve()
    }
    const auth0 = auth0Manager.getClient()

    return auth0.loginWithRedirect({
      authorizationParams: {
        screen_hint: 'signup',
        login_hint: opts?.email
      }
    })
  }

  getAccessToken() {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      throw new Error('No access token found');
    }
    return accessToken;
  }

  getProfile(cb) {
    let accessToken = this.getAccessToken();
    this.auth0.client.userInfo(accessToken, (err, profile) => {
      if (profile) {
        this.userProfile = profile;
      }
      cb(err, profile);
    });
  }

  getUserId() {
    // Get user id throughout the application
    if (this.isAuthenticated()) {
      return localStorage.getItem('userId');
    }
  }

  getUserName() {
    // Get user id throughout the application
    if (this.isAuthenticated()) {
      return localStorage.getItem('name');
    }
  }
  
  getEmail() {
    // Get user id throughout the application
    if (this.isAuthenticated()) {
      return localStorage.getItem('email');
    }
  }
  
  isAuthenticated() {
    // Check whether the current time is past the access token's expiry time
    let expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return new Date().getTime() < expiresAt;
  }

  isFeatureEnabled(featureFlag) {
    if (!this.isAuthenticated()) return false

    //
    // LOCALLY, LOCAL_FEATURE_TOGGLES IS THE ANSWER, not what happens to be in
    // localStorage.
    //
    // Toggles are written once, by seedLocalSession, at login. So adding a flag
    // to the list above does nothing for any session that already exists: the
    // developer who added it sees the flagged path stay invisible, with no
    // error and nothing in the console, and the only fix is to log out or clear
    // localStorage by hand. That cost real time on #3229.
    //
    // Reading the list directly makes the declared local toggles authoritative
    // the moment the module reloads. Local-only: LOCAL_AUTH_BYPASS is false in
    // every build, so production still reads the Auth0 claim from localStorage.
    //
    if (LOCAL_AUTH_BYPASS) return localToggles().includes(featureFlag)

    try {
      return JSON.parse(localStorage.getItem('edyn-featureToggles') || '[]').includes(featureFlag)
    } catch (e) {
      console.error(e)

      return false;
    }
  }

  userHasAccess(userType) {
    // check the path user tried to access is authorized or not
    const userAccess = localStorage.getItem('userType');
    if (userAccess) {
      return userType.every((type) => userAccess.includes(type));
    }
    return false;
  }

  /**
   * `cacheMode` is honoured rather than ignored. It used to be hardcoded to
   * 'off', which forced a network refresh-token exchange on EVERY call.
   *
   * That is a real bug, not just a slow path. The Auth0 client sets
   * `useRefreshTokens: true` and the tenant rotates refresh tokens with reuse
   * detection, so each forced exchange invalidates the token it presented.
   * useFetch calls this once per hook, and a page load fires several
   * concurrently, so two exchanges routinely present the same refresh token.
   * Auth0 then answers the second with
   * `403 invalid_grant: Unknown or invalid refresh token` and revokes the whole
   * family. The SDK usually papers over it with a silent authorization_code
   * round-trip; when that loses the race the app calls loginWithRedirect and
   * the user lands back on the hosted login page mid-session.
   *
   * Measured against staging on 2026-08-20 by rewriting this literal in the
   * deployed bundle: 6 of 7 hard navigations produced the 403 revocation and 1
   * ended at the login screen; with the cache left on, 5 of 5 made no refresh
   * call at all and none bounced. It is also the cause of the e2e suite's
   * `deep link: /portal-carer/placements` flake, 14 occurrences.
   *
   * Callers wanting a genuine server-side check pass 'off' explicitly, as
   * checkIfValidSessionExists does.
   */
  getAccessTokenAsync(cacheMode) {
    if (LOCAL_AUTH_BYPASS) {
      // Return the token for whoever is logged in locally, not a fixed user.
      return Promise.resolve(this.getAccessToken())
    }
    const auth0 = auth0Manager.getClient()
    return auth0.getTokenSilently({detailedResponse: true, ...(cacheMode ? {cacheMode} : {})}).then(async (res) => {
      if (res.access_token !== this.getAccessToken()) {
        const user = await auth0Manager.getClient().getUser()
        this.updateLocalStorage(res, user)  
      }
      return res.access_token
    })
  }
}
